import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Building2, Users, Settings, LogOut, Sun, Moon,
  ChevronRight, ChevronLeft, FileText, ScrollText,
} from 'lucide-react';
import { useAuthStore } from '../store/auth.store.js';
import { useThemeStore } from '../store/theme.store.js';
import { logout as apiLogout } from '../api/auth.js';

const navItems = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, roles: ['superadmin', 'admin'], exact: true },
  // admin (contador) no tiene un listado de clientes — entra directo al
  // detalle del suyo propio, donde está el wizard de certificados.
  { to: '/clients', label: 'Clientes', icon: Building2, roles: ['superadmin', 'admin'] },
  { to: '/cae-requests', label: 'Comprobantes', icon: FileText, roles: ['superadmin', 'admin'] },
  { to: '/operation-logs', label: 'Logs', icon: ScrollText, roles: ['superadmin', 'admin'] },
  { to: '/users', label: 'Usuarios', icon: Users, roles: ['superadmin', 'admin'] },
  { to: '/settings', label: 'Configuración', icon: Settings, roles: ['superadmin'] },
];

const SIDEBAR_W = 240;
const COLLAPSED_W = 64;

export default function Layout() {
  const navigate = useNavigate();
  const { user, clear } = useAuthStore();
  const { darkMode, sidebarCollapsed, toggleDark, toggleSidebar } = useThemeStore();

  const handleLogout = async () => {
    try { await apiLogout(); } catch { /* stateless */ }
    clear();
    navigate('/login', { replace: true });
  };

  const visible = navItems
    .filter((it) => !user || it.roles.includes(user.role))
    .map((it) => (it.to === '/clients' && user?.role === 'admin')
      ? { ...it, to: `/clients/${user.clientId}`, label: 'Mi cuenta' }
      : it);
  const collapsed = sidebarCollapsed;
  const sidebarPx = collapsed ? COLLAPSED_W : SIDEBAR_W;

  return (
    <div className="min-h-screen flex bg-bg relative">
      <aside
        className="sidebar shrink-0 flex flex-col border-r border-border bg-bg-surface overflow-hidden"
        style={{ width: sidebarPx }}
      >
        <div className="h-16 flex items-center border-b border-border px-4 gap-3 overflow-hidden">
          <div className="w-8 h-8 rounded-lg bg-accent flex items-center justify-center shrink-0">
            <span className="text-white font-bold text-sm select-none">A</span>
          </div>
          {!collapsed && (
            <span className="font-semibold text-base whitespace-nowrap text-text-primary">Arca Admin</span>
          )}
        </div>

        <nav className="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto overflow-x-hidden">
          {visible.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''} ${collapsed ? 'justify-center' : ''}`}
              >
                <Icon size={18} className="shrink-0" />
                {!collapsed && <span className="truncate">{item.label}</span>}
              </NavLink>
            );
          })}
        </nav>

        <div className="border-t border-border p-2 space-y-1">
          <button
            onClick={toggleDark}
            title={darkMode ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
            className={`nav-link w-full ${collapsed ? 'justify-center' : ''}`}
          >
            {darkMode ? <Sun size={18} className="shrink-0" /> : <Moon size={18} className="shrink-0" />}
            {!collapsed && <span className="truncate">{darkMode ? 'Modo claro' : 'Modo oscuro'}</span>}
          </button>

          {!collapsed && (
            <div className="px-3 pt-1 pb-0.5">
              <div className="text-xs truncate text-text-secondary">{user?.email}</div>
              <div className="text-xs text-text-muted">{user?.role}</div>
            </div>
          )}

          <button
            onClick={handleLogout}
            title="Cerrar sesión"
            className={`nav-link w-full text-red-400 hover:text-red-300 hover:bg-red-500/10 ${collapsed ? 'justify-center' : ''}`}
          >
            <LogOut size={18} className="shrink-0" />
            {!collapsed && <span>Cerrar sesión</span>}
          </button>
        </div>
      </aside>

      <button
        onClick={toggleSidebar}
        title={collapsed ? 'Expandir menú' : 'Colapsar menú'}
        aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
        className="absolute top-1/2 -translate-y-1/2 z-30 w-6 h-6 rounded-full
                   flex items-center justify-center shadow-md hover:scale-110 active:scale-95
                   transition-[left,transform] duration-200"
        style={{
          left: `${sidebarPx - 12}px`,
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-strong)',
          color: 'var(--text-muted)',
        }}
      >
        {collapsed ? <ChevronRight size={12} strokeWidth={2.5} /> : <ChevronLeft size={12} strokeWidth={2.5} />}
      </button>

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 shrink-0 border-b border-border bg-bg-surface/80 backdrop-blur px-6 flex items-center justify-between gap-4">
          <h1 className="text-sm font-medium text-text-muted">Panel de administración — arca-api</h1>
        </header>
        <div className="flex-1 overflow-auto p-6">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
